import { useState } from "react";
import crossIcon from "src/assets/cross.svg";
import { Button, Modal } from "src/components";

interface SelectCounterModalProps {
  isOpen: boolean;
  onClose: () => void;
  counterList: any[];
  tempSelectedCounter: any;
  setTempSelectedCounter: (value: any) => void;
  onConfirm: () => void;
  allowCustomerNameEntry?: boolean;
}

const SelectCounterModal = ({ isOpen, onClose, counterList, tempSelectedCounter, setTempSelectedCounter, onConfirm, allowCustomerNameEntry = false }: SelectCounterModalProps) => {
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState<"counter" | "customer">(tempSelectedCounter?.isCustomerEntry ? "customer" : "counter");
  const [customerName, setCustomerName] = useState(tempSelectedCounter?.isCustomerEntry ? tempSelectedCounter?.customer_name || "" : "");

  const filteredList = (counterList || []).filter((item) => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return [item?.counter_name, item?.counter_code, item?.gst_number, item?.dl_number, item?.hospital_name].some((value) =>
      value?.toString().toLowerCase().includes(query)
    );
  });

  const switchMode = (nextMode: "counter" | "customer") => {
    if (nextMode === mode) return;
    setMode(nextMode);
    setTempSelectedCounter(null);
    if (nextMode === "customer") setCustomerName("");
  };

  const handleCustomerNameChange = (value: string) => {
    setCustomerName(value);
    setTempSelectedCounter(value.trim() ? { customer_name: value, isCustomerEntry: true } : null);
  };

  const isConfirmDisabled =
    mode === "customer" ? !tempSelectedCounter?.customer_name?.trim() : !tempSelectedCounter || tempSelectedCounter?.isCustomerEntry;

  return (
    <Modal isOpen={isOpen} isHeader={false} onCloseModal={onClose}>
      <div className="flex flex-col gap-y-2 mb-4 w-[900px] max-w-[95vw] max-h-[85vh] mx-auto px-6 bg-white p-4 rounded-xl">
        <div className="flex justify-between items-center">
          <p className="text-lg font-semibold text-gray-800">{mode === "customer" ? "Enter Customer Name" : "Select Counter"}</p>
          <Button className="bg-transparent -mr-3" onClick={onClose}>
            <img src={crossIcon} alt="Close" className="w-5 h-5 invert cursor-pointer" />
          </Button>
        </div>

        <p className="text-sm text-gray-600 mt-2">
          {mode === "customer" ? "Enter the customer name for this invoice." : "Select the counter for this invoice."}
        </p>

        {allowCustomerNameEntry && (
          <div className="flex gap-4 mt-2 border-b border-gray-200">
            <button
              type="button"
              onClick={() => switchMode("counter")}
              className={`text-sm pb-2 px-1 -mb-px border-b-2 ${mode === "counter" ? "font-semibold text-[#8051FF] border-[#8051FF]" : "text-gray-500 border-transparent"
                }`}
            >
              Select Counter
            </button>
            <button
              type="button"
              onClick={() => switchMode("customer")}
              className={`text-sm pb-2 px-1 -mb-px border-b-2 ${mode === "customer" ? "font-semibold text-[#8051FF] border-[#8051FF]" : "text-gray-500 border-transparent"
                }`}
            >
              Enter Customer Name
            </button>
          </div>
        )}

        {mode === "customer" ? (
          <div className="flex flex-col gap-1 mt-2">
            <label className="text-sm font-medium text-gray-700">Customer Name</label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => handleCustomerNameChange(e.target.value)}
              placeholder="Enter customer name"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-purple-400"
            />
          </div>
        ) : (
          <>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by counter name, code, hospital name, GST or DL number"
              className="w-full mt-2 px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-purple-400"
            />

            <div className="overflow-y-auto mt-2 border border-gray-200 rounded-lg" style={{ maxHeight: "50vh" }}>
              <table className="w-full text-sm text-left border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="sticky top-0 z-10 bg-gray-100 p-2 border-b border-gray-200 whitespace-nowrap">Counter Name</th>
                    <th className="sticky top-0 z-10 bg-gray-100 p-2 border-b border-gray-200 whitespace-nowrap">Counter Code</th>
                    <th className="sticky top-0 z-10 bg-gray-100 p-2 border-b border-gray-200 whitespace-nowrap">Hospital Name</th>
                    <th className="sticky top-0 z-10 bg-gray-100 p-2 border-b border-gray-200 whitespace-nowrap">GST No.</th>
                    <th className="sticky top-0 z-10 bg-gray-100 p-2 border-b border-gray-200 whitespace-nowrap">DL No.</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredList.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-4 text-center text-gray-500">
                        No counters found
                      </td>
                    </tr>
                  )}
                  {filteredList.map((item) => {
                    const isSelected = tempSelectedCounter?.counter_location_id === item?.counter_location_id;
                    return (
                      <tr
                        key={item?.counter_location_id}
                        onClick={() => setTempSelectedCounter(item)}
                        className={`cursor-pointer ${isSelected ? "bg-purple-200 font-semibold" : "hover:bg-gray-50"}`}
                      >
                        <td className="p-2 border-b border-gray-100">{item?.counter_name}</td>
                        <td className="p-2 border-b border-gray-100">{item?.counter_code}</td>
                        <td className="p-2 border-b border-gray-100">{item?.hospital_name || "-"}</td>
                        <td className="p-2 border-b border-gray-100">{item?.gst_number || "-"}</td>
                        <td className="p-2 border-b border-gray-100">{item?.dl_number || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tempSelectedCounter && (
          <p className="text-xs text-gray-500 mt-1">
            Selected:{" "}
            <span className="font-semibold text-gray-700">
              {tempSelectedCounter?.isCustomerEntry
                ? tempSelectedCounter?.customer_name
                : `${tempSelectedCounter?.counter_name} - ${tempSelectedCounter?.counter_code}`}
            </span>
          </p>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Button className="bg-gray-300 text-gray-700 hover:bg-[#BFC5CC] px-4 py-2 rounded-lg" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="text-white bg-[#8051FF] hover:bg-[#6E3DFF] px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onConfirm}
            disabled={isConfirmDisabled}
          >
            Confirm
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SelectCounterModal;
